import type { AiSettings } from '../types';
import { LANGUAGES, TONES } from '../types';
import Toggle from './Toggle';

interface Props {
  form: AiSettings;
  set: <K extends keyof AiSettings>(key: K, val: AiSettings[K]) => void;
}

export default function IdentityTab({ form, set }: Props) {
  return (
    <div className="space-y-5">

      {/* Enable / Disable */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 flex items-center justify-center rounded-xl transition-all"
              style={{
                backgroundColor: form.assistant_enabled
                  ? 'rgba(200,169,110,0.12)'
                  : '#f8fafc',
              }}
            >
              <i
                className="ri-robot-line text-xl"
                style={{ color: form.assistant_enabled ? '#C8A96E' : '#cbd5e1' }}
              />
            </div>
            <div>
              <p className="text-[#0f172a] font-semibold text-sm">AI Sales Assistant</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    form.assistant_enabled ? 'bg-emerald-400' : 'bg-slate-300'
                  }`}
                />
                <span
                  className={`text-xs transition-colors ${
                    form.assistant_enabled ? 'text-emerald-500' : 'text-slate-400'
                  }`}
                >
                  {form.assistant_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
          <Toggle enabled={form.assistant_enabled} onChange={(v) => set('assistant_enabled', v)} />
        </div>

        <div className="mt-4 flex items-start gap-2.5 px-3.5 py-3 bg-amber-50 rounded-xl border border-amber-200/60">
          <i className="ri-information-line text-amber-500 text-xs mt-0.5 shrink-0" />
          <p className="text-amber-700/80 text-[11px] leading-relaxed">
            OpenAI / Anthropic keys must be stored in{' '}
            <span className="font-mono font-semibold">Supabase Edge Function secrets</span>. This page
            configures behavior only — no AI calls are made yet.
          </p>
        </div>
      </div>

      {/* Name + Language */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-user-star-line text-[#C8A96E]" />
          </span>
          Assistant Identity
        </h3>

        <div>
          <label className="text-slate-500 text-xs font-medium mb-1.5 block">
            Assistant Name
          </label>
          <input
            type="text"
            value={form.assistant_name}
            onChange={(e) => set('assistant_name', e.target.value)}
            placeholder="e.g. Luna, Aria, Studio Assistant…"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all"
          />
          <p className="text-slate-400 text-[11px] mt-1">
            Name shown to clients when the assistant introduces itself.
          </p>
        </div>

        <div>
          <label className="text-slate-500 text-xs font-medium mb-1.5 block">
            Response Language
          </label>
          <div className="relative">
            <select
              value={form.language}
              onChange={(e) => set('language', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/60 transition-all cursor-pointer appearance-none"
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <p className="text-slate-400 text-[11px] mt-1">
            Language the assistant will use by default in conversations.
          </p>
        </div>
      </div>

      {/* Tone */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2 mb-4">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-palette-line text-[#C8A96E]" />
          </span>
          Conversation Tone
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {TONES.map((t) => {
            const active = form.tone === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => set('tone', t.value)}
                className={`flex items-start gap-3 p-3.5 rounded-xl border transition-all cursor-pointer text-left ${
                  active
                    ? 'border-[#C8A96E]/50 bg-[#C8A96E]/8'
                    : 'border-slate-200 bg-slate-50/50 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5"
                  style={{
                    backgroundColor: active ? 'rgba(200,169,110,0.15)' : '#f1f5f9',
                  }}
                >
                  <i
                    className={`${t.icon} text-sm`}
                    style={{ color: active ? '#C8A96E' : '#94a3b8' }}
                  />
                </div>
                <div>
                  <p
                    className={`text-xs font-semibold mb-0.5 ${
                      active ? 'text-[#C8A96E]' : 'text-slate-600'
                    }`}
                  >
                    {t.label}
                  </p>
                  <p className="text-slate-400 text-[11px] leading-snug">{t.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}